var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { select, pointer } from 'd3';
import throttle from 'lodash.throttle';
import styled from 'styled-components';
import { getCoordValues } from './utils';
import { palette } from '../../constants';
import { Container } from './TrackArea.styles';
import { useCanvas } from '../components/Canvas';
import { sortSeriesByName } from '../utils/sortByName';
import { useCharts } from '../components/Charts';
// TODO: Resolve with existing TrackLine components separately.
var Line = styled.line(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  pointer-events: none;\n  stroke: ", ";\n  stroke-width: 2;\n"], ["\n  pointer-events: none;\n  stroke: ", ";\n  stroke-width: 2;\n"])), palette.orange7);
export function TrackArea(_a) {
    var _b, _c;
    var pinId = _a.pinId, _d = _a.drawTrackLine, drawTrackLine = _d === void 0 ? true : _d, xScale = _a.xScale, height = _a.height, series = _a.series, accessors = _a.accessors, meta = _a.meta, Tooltip = _a.tooltip, _e = _a.tipWidth, tipWidth = _e === void 0 ? 240 : _e, colorScheme = _a.colorScheme, _f = _a.ignoreVertical, ignoreVertical = _f === void 0 ? false : _f;
    var _g = useCharts(), pointXY = _g.pointXY, setPinned = _g.setPinned;
    var _h = useCanvas(), margins = _h.margins, boundedWidth = _h.boundedWidth, boundedHeight = _h.boundedHeight;
    var sorted = React.useMemo(function () {
        if (!Array.isArray(series))
            return [series];
        return sortSeriesByName(series, accessors);
    }, [series, accessors]);
    var tipContainerRef = React.useRef(null);
    var tipContentRef = React.useRef(null);
    var rectRef = React.useRef(null);
    var scrubRef = React.useRef(null);
    var scrubEl = React.useMemo(function () {
        return scrubRef.current !== null ? select(scrubRef.current) : null;
    }, [scrubRef.current]);
    var tipContainerEl = React.useMemo(function () {
        return tipContainerRef.current !== null
            ? select(tipContainerRef.current)
            : null;
    }, [tipContainerRef.current]);
    var tipContentEl = React.useMemo(function () {
        return tipContentRef.current !== null ? select(tipContentRef.current) : null;
    }, [tipContentRef.current]);
    var throttledGetCoordValues = React.useCallback(throttle(getCoordValues, 20, {
        leading: true
    }), []);
    var handleMouseMove = React.useCallback(function (event) {
        var _a;
        var _b = (_a = pointer(event, rectRef.current)) !== null && _a !== void 0 ? _a : [0, 0], mouseX = _b[0], mouseY = _b[1];
        // TODO: x value/date from mouse
        var vals = throttledGetCoordValues({
            rect: rectRef.current,
            series: sorted,
            accessors: accessors,
            xScale: xScale,
            mouseX: mouseX,
            mouseY: mouseY,
            colorScheme: colorScheme
        });
        // Show/Hide scrub line
        scrubEl === null || scrubEl === void 0 ? void 0 : scrubEl.attr('opacity', vals === undefined ? 0 : 1).attr('x1', "" + mouseX).attr('x2', "" + mouseX);
        // Show/Hide tooltip
        tipContainerEl === null || tipContainerEl === void 0 ? void 0 : tipContainerEl.attr('opacity', vals === undefined ? 0 : 1);
        var tipContentNode = tipContentRef.current;
        if (vals === undefined)
            return;
        if (tipContainerEl === null)
            return;
        if (tipContentEl === null)
            return;
        if (Tooltip === undefined)
            return;
        if (tipContentNode === null)
            return;
        // Keep tooltip within bounds of the chart:
        // if overflowing right, switch tooltip to be on the left of the cursor
        var x = mouseX + tipWidth > boundedWidth
            ? mouseX - (tipWidth + 10)
            : mouseX + 10;
        // if overflowing bottom, stick bottom of the tooltip to the bottom of the chart
        var y = mouseY + tipContentNode.getBoundingClientRect().height < boundedHeight
            ? mouseY
            : boundedHeight - tipContentNode.getBoundingClientRect().height;
        // Set tooltip container position through dom (faster)
        tipContainerEl.attr('x', "" + x).attr('y', "" + y);
        // Magic sauce to convert JSX into innerHTML
        // Significantly faster than mounting a new root with `render`
        tipContentEl.html(renderToStaticMarkup(React.createElement(Tooltip, __assign({}, vals, { accessors: accessors, meta: meta }))));
        // NOTE: this is to force the object to get cleaned from memory, telling the engine that it's safe to
        // discard this variable.
        vals = undefined;
        return undefined;
    }, [series, accessors, xScale, boundedWidth, height, margins]);
    React.useEffect(function () {
        if (pointXY !== undefined && pinId !== undefined) {
            var vals = getCoordValues({
                series: sorted,
                accessors: accessors,
                xScale: xScale,
                mouseX: pointXY.x,
                mouseY: pointXY.y,
                colorScheme: colorScheme
            });
            setPinned(pinId, vals);
        }
    }, [pointXY]);
    return (React.createElement(Container, null,
        React.createElement(Line, { className: "Chart--ScrubLine", ref: scrubRef, x1: 0, x2: 0, y1: 0, y2: Math.min(boundedHeight, height), stroke: palette.orange7, opacity: 0 }),
        React.createElement(Line, { className: "Chart--TrackLine", x1: (_b = pointXY === null || pointXY === void 0 ? void 0 : pointXY.x) !== null && _b !== void 0 ? _b : 0, x2: (_c = pointXY === null || pointXY === void 0 ? void 0 : pointXY.x) !== null && _c !== void 0 ? _c : 0, y1: 0, y2: Math.min(boundedHeight, height), stroke: palette.orange7, strokeWidth: 2, opacity: pointXY !== undefined && (pinId !== undefined || drawTrackLine)
                ? 1
                : 0 }),
        React.createElement("rect", { className: "Chart--TrackArea", ref: rectRef, onMouseMove: handleMouseMove, x: 0, y: 0, width: boundedWidth, height: ignoreVertical ? height : height - margins.top - margins.bottom, opacity: 0 }),
        React.createElement("foreignObject", { ref: tipContainerRef, width: tipWidth + "px", height: "1px", className: "Chart--ToolTipContainer", overflow: "visible", pointerEvents: "none" },
            React.createElement("div", { className: "Chart--TooltipContent", ref: tipContentRef }))));
}
var templateObject_1;
//# sourceMappingURL=TrackArea.js.map